import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { FIELD_LABEL } from "@/features/clients/components/profile-form-shared";
import { CheckboxField, SelectField } from "@/features/clients/components/record-form-fields";
import { vehiclesApi } from "@/features/worksheets/api";
import { CrudDialog } from "@/features/worksheets/components/crud-dialog";
import { MoneyField } from "@/features/worksheets/components/money-field";
import { centsToDollars, dollarsToCents } from "@/features/worksheets/money";
import type { Vehicle } from "@/features/worksheets/types";

interface VehicleFormValues {
  description: string;
  title: string;
  year: string;
  make: string;
  model: string;
  currentValue: string;
  loanBalance: string;
  leaseLoan: string;
  incomeGen: boolean;
}

const LEASE_LOAN_OPTIONS = [
  { value: "OWN", label: "Own" },
  { value: "LEASE", label: "Lease" },
];

const validationSchema = Yup.object({
  description: Yup.string().required("Description is required"),
});

function toInitialValues(record: Vehicle | null): VehicleFormValues {
  return {
    description: record?.description ?? "",
    title: record?.title ?? "",
    year: record?.year != null ? String(record.year) : "",
    make: record?.make ?? "",
    model: record?.model ?? "",
    currentValue: record ? centsToDollars(record.currentValue) : "",
    loanBalance: record ? centsToDollars(record.loanBalance) : "",
    leaseLoan: record?.leaseLoan ?? "OWN",
    incomeGen: record?.incomeGen ?? false,
  };
}

function toPayload(v: VehicleFormValues): Record<string, unknown> {
  return {
    description: v.description,
    title: v.title,
    year: v.year.trim() === "" ? null : Number(v.year),
    make: v.make,
    model: v.model,
    currentValue: dollarsToCents(v.currentValue),
    loanBalance: dollarsToCents(v.loanBalance),
    leaseLoan: v.leaseLoan,
    incomeGen: v.incomeGen,
  };
}

export function VehicleDialog({
  caseId,
  record,
  open,
  onOpenChange,
  onSaved,
}: {
  caseId: string;
  record: Vehicle | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSaved: () => void;
}) {
  async function onSave(values: VehicleFormValues) {
    const payload = toPayload(values);
    if (record) {
      await vehiclesApi.update(caseId, record.id, payload);
    } else {
      await vehiclesApi.create(caseId, payload);
    }
  }

  return (
    <CrudDialog
      open={open}
      onOpenChange={onOpenChange}
      editing={record !== null}
      noun="Vehicle"
      initialValues={toInitialValues(record)}
      validationSchema={validationSchema}
      onSave={onSave}
      onSaved={onSaved}
    >
      <FormField
        name="description"
        label="Description"
        labelClassName={FIELD_LABEL}
        placeholder="e.g. Daily driver"
      />
      <div className="grid grid-cols-2 gap-4">
        <FormField name="title" label="Title" labelClassName={FIELD_LABEL} />
        <FormField
          name="year"
          label="Year"
          labelClassName={FIELD_LABEL}
          inputMode="numeric"
          placeholder="e.g. 2020"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <FormField name="make" label="Make" labelClassName={FIELD_LABEL} />
        <FormField name="model" label="Model" labelClassName={FIELD_LABEL} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <MoneyField name="currentValue" label="Current Value" />
        <MoneyField name="loanBalance" label="Loan Balance" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <SelectField name="leaseLoan" label="Own / Lease" options={LEASE_LOAN_OPTIONS} />
      </div>
      <CheckboxField name="incomeGen" label="Income-generating" />
    </CrudDialog>
  );
}
